/**
 * Client-side scan matching for mission parcels.
 * Barcode values are compared to existing parcel identifiers only:
 * `parcel_no` on the parcel and `parcelNo` on the task activity.
 */

/**
 * @param {unknown} value
 * @returns {string}
 */
export const normalizeParcelCode = (value) => {
  if (value == null) return '';
  return String(value).trim();
};

/**
 * @param {object | null | undefined} parcel
 * @param {object | null | undefined} activity
 * @returns {string[]}
 */
export const getParcelCodes = (parcel, activity) => {
  const codes = [
    parcel?.parcel_no,
    activity?.parcelNo,
    activity?.parcel?.parcel_no,
  ];

  return codes.map(normalizeParcelCode).filter(Boolean);
};

/**
 * Stable key for a parcel row. Falls back to the list index when no code exists.
 * @param {object | null | undefined} parcel
 * @param {object | null | undefined} activity
 * @param {number} index
 * @returns {string}
 */
export const getParcelScanKey = (parcel, activity, index) => {
  const code = getParcelCodes(parcel, activity)[0];
  return code || `index:${index}`;
};

/**
 * @param {{
 *   parcels?: object[] | null,
 *   activities?: object[] | null,
 *   scannedKeys?: string[],
 *   code?: unknown,
 * }} input
 * @returns {{
 *   status: 'empty' | 'not_in_mission' | 'duplicate' | 'scanned',
 *   scannedKeys: string[],
 *   key?: string,
 *   index?: number,
 * }}
 */
export const applyParcelScan = ({
  parcels,
  activities,
  scannedKeys,
  code,
} = {}) => {
  const currentKeys = Array.isArray(scannedKeys) ? scannedKeys : [];
  const normalized = normalizeParcelCode(code);

  if (!normalized) {
    return { status: 'empty', scannedKeys: currentKeys };
  }

  const parcelList = Array.isArray(parcels) ? parcels : [];
  const activityList = Array.isArray(activities) ? activities : [];
  const index = parcelList.findIndex((parcel, parcelIndex) =>
    getParcelCodes(parcel, activityList[parcelIndex]).includes(normalized)
  );

  if (index < 0) {
    return { status: 'not_in_mission', scannedKeys: currentKeys };
  }

  const key = getParcelScanKey(parcelList[index], activityList[index], index);

  if (currentKeys.includes(key)) {
    return { status: 'duplicate', scannedKeys: currentKeys, key, index };
  }

  return {
    status: 'scanned',
    scannedKeys: [...currentKeys, key],
    key,
    index,
  };
};

/**
 * Find which loaded task owns a barcode.
 * @param {{ taskId: string, parcels?: object[], activities?: object[] }[]} tasks
 * @param {unknown} code
 * @param {string[]} scannedKeys
 */
export const matchBarcodeAcrossTasks = (tasks, code, scannedKeys = []) => {
  const list = Array.isArray(tasks) ? tasks : [];

  for (const task of list) {
    const outcome = applyParcelScan({
      parcels: task?.parcels,
      activities: task?.activities,
      scannedKeys,
      code,
    });

    if (outcome.status === 'empty') return outcome;
    if (outcome.status === 'scanned' || outcome.status === 'duplicate') {
      return { ...outcome, taskId: task?.taskId };
    }
  }

  return {
    status: normalizeParcelCode(code) ? 'not_in_mission' : 'empty',
    scannedKeys: Array.isArray(scannedKeys) ? scannedKeys : [],
  };
};
